import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { JobseekerAppliesService } from 'src/app/jobseeker/shared/services/jobseeker-applies.service';
import { AnnouncementService, ViewAnnouncementModel } from 'src/app/announcement/shared/services/announcement.service';
import { selectAnnViewCountIgnorePeriod, selectCurrentWebsite, selectGeneralDefaultNumberOfListItemsOnPage } from '../../selectors';
import { AppState } from '../../state';
import * as JobseekerAppliesActions from './actions';
import { createErrorMessage, createSuccessMessage, CustomSnackbarService } from '@hrra/ui';
import { TranslateService } from '@ngx-translate/core';
import { displaySnackBarMessage } from '../../actions';
import { HRRAExceptionCode } from '@hrra/common-models';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { SsrService } from '@hrra/core';
import { AuthService } from 'src/app/core/auth.service';
import { AnnouncementViewEvent } from 'src/app/announcement/shared/models/announcement-details-model';

@Injectable()
export class JobseekerAppliesEffects {
  loadAnnouncementApplications$ = createEffect(() =>
    combineLatest([
      this.actions$.pipe(ofType(JobseekerAppliesActions.loadApplications)),
      this.store.pipe(
        select(selectGeneralDefaultNumberOfListItemsOnPage),
        filter((c) => c !== undefined),
        take(1)
      ),
    ]).pipe(
      mergeMap(([action, pageSize]) =>
        this.service.getApplicationList(action.payload.page, pageSize).pipe(
          map((resp) =>
            JobseekerAppliesActions.loadApplicationsSuccess({
              payload: { application: resp.data, page: action.payload.page },
            })
          ),
          catchError((response) =>
            of(
              JobseekerAppliesActions.loadApplicationsFail({
                payload: response.error,
              })
            )
          )
        )
      )
    )
  );

  loadCvBoxApplications$ = createEffect(() =>
    combineLatest([
      this.actions$.pipe(ofType(JobseekerAppliesActions.loadCvBoxApplications)),
      this.store.pipe(
        select(selectGeneralDefaultNumberOfListItemsOnPage),
        filter((c) => c !== undefined),
        take(1)
      ),
    ]).pipe(
      mergeMap(([action, pageSize]) =>
        this.service
          .getCvBoxApplicationList(action.payload.page, pageSize)
          .pipe(
            map((resp) =>
              JobseekerAppliesActions.loadCvBoxApplicationsSuccess({
                payload: { application: resp.data, page: action.payload.page },
              })
            ),
            catchError((response) =>
              of(
                JobseekerAppliesActions.loadCvBoxApplicationsFail({
                  payload: response.error,
                })
              )
            )
          )
      )
    )
  );

  loadInvites$ = createEffect(() =>
    combineLatest([
      this.actions$.pipe(ofType(JobseekerAppliesActions.loadInvites)),
      this.store.pipe(
        select(selectGeneralDefaultNumberOfListItemsOnPage),
        filter((c) => c !== undefined),
        take(1)
      ),
    ]).pipe(
      mergeMap(([action, pageSize]) => {
        const start = (action.payload.page - 1) * pageSize;
        return this.service.getInviteList(start, action.payload.filter).pipe(
          map((resp) =>
            JobseekerAppliesActions.loadInvitesSuccess({
              payload: {
                jobseekerInviteResponse: resp.data,
                page: action.payload.page,
              },
            })
          ),
          catchError((response) =>
            of(
              JobseekerAppliesActions.loadInvitesFail({
                payload: response.error,
              })
            )
          )
        );
      })
    )
  );

  loadInviteAnnouncement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobseekerAppliesActions.loadInviteAnnouncement),
      mergeMap((action) =>
        this.announcementService
          .getAnnouncement(action.payload.announcementId)
          .pipe(
            map((resp) =>
              JobseekerAppliesActions.loadInviteAnnouncementSuccess({
                payload: { announcementDetails: resp.data },
              })
            ),
            catchError((response) =>
              of(
                JobseekerAppliesActions.loadInviteAnnouncementFail({
                  payload: response.error,
                })
              )
            )
          )
      )
    )
  );

  loadInviteAnnouncementSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobseekerAppliesActions.loadInviteAnnouncementSuccess),
      filter(() => this.ssrService.isPlatformBrowser()),
      switchMap((action) =>
        this.store.select(selectCurrentWebsite).pipe(
          filter((providerId) => providerId != null),
          map((providerId) => ({ action, providerId }))
        )
      ),
      map(({ action, providerId }) =>
        JobseekerAppliesActions.sendAnnouncementViewToAnalytic({
          payload: {
            announcement: action.payload.announcementDetails.announcement,
            providerWebsiteId: providerId.providerWebsiteId,
          },
        })
      )
    )
  );

  sendAnnouncementViewToAnalytic$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobseekerAppliesActions.sendAnnouncementViewToAnalytic),
      tap((action) => {
        const announcementAnalytic: AnnouncementViewEvent = {
          announcementId: action.payload.announcement.announcementId,
          announcementStatId: action.payload.announcement.announcementStatId,
          announcementRenewalStatId:
            action.payload.announcement.announcementRenewalStatId,
          providerWebsiteId: action.payload.providerWebsiteId,
          userId: this.authService.getUserId(),
          isAnonymous: !this.authService.isSignedIn(),
          isUnique: this.announcementService.isAnnouncementViewCountable(
            action.payload.announcement.announcementId
          ),
        };

        this.announcementService.sendAnnouncementViewToAnalytic(
          announcementAnalytic
        );
      }),
      map((action) =>
        JobseekerAppliesActions.viewAnnouncement({
          payload: {
            announcementId: action.payload.announcement.announcementId,
            isUniqueView: this.announcementService.isAnnouncementViewCountable(
              action.payload.announcement.announcementId
            ),
          },
        })
      ),
      catchError((response) =>
        of(
          JobseekerAppliesActions.sendAnnouncementViewToAnalyticFail({
            payload: response.error,
          })
        )
      )
    )
  );

  viewAnnouncement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobseekerAppliesActions.viewAnnouncement),
      withLatestFrom(this.store.pipe(select(selectAnnViewCountIgnorePeriod))),
      tap(([action, ignorePeriod]) => {
        const currentDate = new Date();
        const viewData: ViewAnnouncementModel = {
          announcementId: action.payload.announcementId,
          expireDate: new Date(currentDate.getTime() + ignorePeriod * 60000),
        };
        this.announcementService.addAnnouncementView(viewData);
      }),
      map(() => JobseekerAppliesActions.viewAnnouncementSuccess())
    )
  );

  markInviteViewed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobseekerAppliesActions.markInviteViewed),
      mergeMap((action) =>
        this.service.markInviteViewed(action.payload.inviteId).pipe(
          map(() =>
            JobseekerAppliesActions.markInviteViewedSuccess({
              payload: { inviteId: action.payload.inviteId },
            })
          ),
          catchError((response) =>
            of(
              JobseekerAppliesActions.markInviteViewedFail({
                payload: response.error,
              })
            )
          )
        )
      )
    )
  );

  respondToInvite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobseekerAppliesActions.respondToInvite),
      mergeMap((action) =>
        this.service
          .respondToInvite(
            action.payload.inviteId,
            action.payload.responseStatus
          )
          .pipe(
            map(() =>
              JobseekerAppliesActions.respondToInviteSuccess({
                payload: action.payload,
              })
            ),
            catchError((response) =>
              of(
                JobseekerAppliesActions.respondToInviteFail({
                  payload: response.error,
                })
              )
            )
          )
      )
    )
  );

  respondToInviteFail$ = createEffect(() => this.actions$.pipe(
      ofType(JobseekerAppliesActions.respondToInviteFail),
      tap((error) => {
         const incompleteProfile = error.payload.errorCode === HRRAExceptionCode.ProfileIsIncomplete || error.payload.errorCode === HRRAExceptionCode.ProfileIsPrivate || error.payload.errorCode === HRRAExceptionCode.CustomerIsBlackListed

         if(incompleteProfile){
          this.dialogService.getApplyFailedDialog(error.payload.errorCode)
         }
         else{
          const text = this.translate.instant('message.AlreadyResponded');
          this.store.dispatch(displaySnackBarMessage({payload: createErrorMessage(text, '', 4000)}))
         }
      }
      )
  ), { dispatch: false });

  respondToInvitesuccess$ = createEffect(() => this.actions$.pipe(
      ofType(JobseekerAppliesActions.respondToInviteSuccess),
      tap(() => {
         const text = this.translate.instant('message.RespondedToAnnouncement');
         this.store.dispatch(displaySnackBarMessage({payload: createSuccessMessage(text, '', 4000)}))
      }
      )
  ), { dispatch: false });

  constructor(
    private actions$: Actions,
    private service: JobseekerAppliesService,
    private announcementService: AnnouncementService,
    private store: Store<AppState>,
    private customSnackBarService: CustomSnackbarService,
    private translate: TranslateService,
    private dialogService: DialogService,
    private ssrService: SsrService,
    private authService: AuthService
  ) {}
}
